import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { MapRenderState } from '../../helpers/constants';
import { getDashArray } from '../../helpers/utils';
import { internalMapLoaded_AC } from '../../store/actions/executor.actions';
import useMapImage from './hooks/useMapImage';
import NewTrack from './NewTrack';
import Polygon from './Polygons';
import SimplePoint from './SimplePoint';
import WayPoint from './WayPoint';

export interface IMapProps {
  monitor?: string;
  svgWidth: string;
  svgHeight: string;
}
interface Position {
  x?: number;
  y?: number;
}

const StyledPath = styled.path<any>`
  ${({ duration }) => `
    transition: d ${duration}ms ease;
`};
`;

const MapImageDiv = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 100%;
  pointer-events: none;
  & > img {
    width: 100%;
    height: 100%;
  }
`;

const SVGContainer = styled.div<Position>`
  position: absolute;
  left: 0;
  top: 0;
`;

export default function Map(props: IMapProps) {
  const { monitor = 'upper', svgWidth, svgHeight } = props;
  const dispatch = useDispatch();
  const uiMapState = useSelector(
    (state: any) => state.executor.uiState.mapRender
  );
  const tracks = useSelector((state: any) => state.executor.tracks);
  const trackConnectors = useSelector(
    (state: any) => state.executor.trackConnector
  );
  const [trackConnectorPaths, setTrackConnectorPaths] = useState([]);
  const wayPoints = useSelector((state: any) => state.executor.wayPoints);

  const simplePoints = useSelector((state: any) => state.executor.simplePoints);

  const polygons = useSelector((state: any) => state.executor.polygons);

  useEffect(() => {
    setTrackConnectorPaths(
      trackConnectors.map((trackConnector: any) => {
        const sourceTrack = tracks.find(
          (t: any) => t.trackNumber === trackConnector.sourceTrack
        );
        const targetTrack = tracks.find(
          (t: any) => t.trackNumber === trackConnector.targetTrack
        );
        return {
          start: {
            ...sourceTrack.animatedPosition,
            lineColor: trackConnector.lineColor,
            lineType: trackConnector.lineType,
          },
          end: {
            ...targetTrack.animatedPosition,
            lineColor: trackConnector.lineColor,
            lineType: trackConnector.lineType,
          },
        };
      })
    );
  }, [tracks, trackConnectors]);

  const { url } = useMapImage(monitor);

  return (
    <div>
      {/* {tracks.map((track: any, index: number) => (
        <Track key={index} {...track} />
      ))} */}
      <MapImageDiv>
        {url && (
          <img
            onLoad={() => {
              // Do not fire on repait, only fire this action when we have started rendering a new map and have compleated loading it
              if (
                uiMapState &&
                uiMapState[monitor] === MapRenderState.STARTED
              ) {
                dispatch(internalMapLoaded_AC(monitor));
              }
            }}
            src={url}
          />
        )}
      </MapImageDiv>
      {url && (
        <SVGContainer>
          <svg width={svgWidth} height={svgHeight}>
            {Object.keys(wayPoints).map((labelKey: string) => {
              const wayPoint = wayPoints[labelKey];
              return (
                <WayPoint
                  key={wayPoint.labelReference}
                  {...wayPoint}
                  monitor={monitor}
                />
              );
            })}
            {simplePoints.map((point: any, index: number) => {
              return <SimplePoint key={index} {...point} monitor={monitor} />;
            })}
            {Object.keys(polygons || {}).map((key: string, index: number) => {
              const { type, ...polygon } = polygons[key];
              return (
                <Polygon
                  key={`polygon-${index}`}
                  polygon={{ ...polygon }}
                  type={type}
                  monitor={monitor}
                />
              );
            })}
            {trackConnectorPaths.map((connector: any, index: number) => (
              <StyledPath
                duration={'400'}
                d={`M ${connector.start.x} ${connector.start.y} L ${connector.end.x} ${connector.end.y}`}
                stroke={
                  connector.start.lineColor ||
                  connector.end.lineColor ||
                  'yellow'
                }
                strokeDasharray={getDashArray(connector.start.lineType)}
                strokeWidth="2"
                key={index}
              />
            ))}

            {tracks.map((track: any, index: number) => {
              const {
                symbol,
                point,
                trackNumber,
                fontColor,
                hookProperties,
                hookSize,
                trackSymbolHeight = '30',
                trackSymbolWidth = '30',
              } = track;
              return (
                <NewTrack
                  {...track}
                  key={`${trackNumber}_${point.latV}_${point.longV}`}
                  resource={symbol}
                  point={point}
                  monitor={monitor}
                  trackNumber={trackNumber}
                  fontColor={fontColor}
                  hookProperties={hookProperties}
                  hookSize={hookSize}
                  trackSymbolHeight={trackSymbolHeight}
                  trackSymbolWidth={trackSymbolWidth}
                />
              );
            })}
          </svg>
        </SVGContainer>
      )}
    </div>
  );
}
